import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAnnoScolastico, formatAnnoScolastico } from "@/lib/annoScolastico";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";
import { Plus, Upload, Pencil, Trash2, X, HelpCircle, FileText, Download, Paperclip, Search, Eye } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const rndToken = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const newForm = () => ({ id: null, data: "", circolare_data: "", circolare_nr: "", oggetto: "", testo: "", fonte: "manuale", import_token: rndToken(), allegati: [] });

// dd/mm/yyyy -> yyyy-mm-dd (per input type=date). Se gia' ISO o non parsabile, ritorna "".
function toIsoDate(s) {
  if (!s) return "";
  const m = String(s).match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  const iso = String(s).match(/(\d{4})-(\d{2})-(\d{2})/);
  return iso ? iso[0] : "";
}
function fmtDate(s) {
  const iso = toIsoDate(s);
  if (!iso) return s || "—";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}
function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function FaqMensaPage() {
  const { anno } = useAnnoScolastico();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // form object or null
  const [detail, setDetail] = useState(null); // FAQ mostrata nel popup dettaglio
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [spaggiariOpen, setSpaggiariOpen] = useState(false);
  const [query, setQuery] = useState("");
  const emlRef = useRef(null);
  const attachRef = useRef(null);

  const load = useCallback(async () => {
    if (!anno) return;
    setLoading(true);
    try {
      const r = await api.get("/faq", { params: { anno } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento FAQ");
    } finally { setLoading(false); }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing(newForm());
  const openEdit = (it) => setEditing({
    id: it.id, data: toIsoDate(it.data), circolare_data: toIsoDate(it.circolare_data),
    circolare_nr: it.circolare_nr || "", oggetto: it.oggetto || "", testo: it.testo || "", fonte: it.fonte || "manuale",
    import_token: rndToken(),
    allegati: (it.allegati || []).map((a) => ({ ...a, saved: true })),
  });

  const save = async () => {
    if (!editing) return;
    if (!editing.oggetto.trim() && !editing.testo.trim()) { toast.error("Inserisci almeno oggetto o testo"); return; }
    setSaving(true);
    try {
      const pending = editing.allegati.filter((a) => !a.saved);
      const base = {
        anno,
        data: editing.data,
        circolare_data: editing.circolare_data,
        circolare_nr: editing.circolare_nr,
        oggetto: editing.oggetto,
        testo: editing.testo,
        fonte: editing.fonte || "manuale",
        import_token: editing.import_token,
      };
      if (editing.id) {
        await api.put(`/faq/${editing.id}`, { ...base, new_allegati_ids: pending.map((a) => a.id) });
      } else {
        await api.post("/faq", { ...base, allegati_ids: pending.map((a) => a.id) });
      }
      toast.success(editing.id ? "FAQ aggiornata" : "FAQ creata");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare la FAQ "${it.oggetto || it.testo?.slice(0, 40) || ""}"?`)) return;
    try {
      await api.delete(`/faq/${it.id}`);
      toast.success("FAQ eliminata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  // --- Import .eml ---
  const onPickEml = () => emlRef.current?.click();
  const onEmlSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const emls = files.filter((f) => f.name.toLowerCase().endsWith(".eml"));
    if (!emls.length) { toast.error("Seleziona uno o più file .eml"); return; }
    setImporting(true);
    try {
      if (emls.length === 1) {
        // Import singolo → apre il form per revisione prima del salvataggio
        const fd = new FormData();
        fd.append("file", emls[0]);
        const r = await api.post("/faq/parse-eml", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing({
          ...newForm(),
          fonte: "eml",
          oggetto: r.data.oggetto || "",
          testo: r.data.testo || "",
          data: toIsoDate(r.data.data_msg) || "",
          import_token: r.data.import_token || rndToken(),
          allegati: (r.data.allegati || []).map((a) => ({ ...a, saved: false })),
        });
        const nAll = (r.data.allegati || []).length;
        toast.success(`Email .eml importata${nAll ? ` (${nAll} allegat${nAll === 1 ? "o" : "i"})` : ""}: verifica e salva`);
      } else {
        // Import multiplo → ogni email crea una FAQ direttamente
        const fd = new FormData();
        fd.append("anno", anno);
        emls.forEach((f) => fd.append("files", f));
        const r = await api.post("/faq/import-eml-bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const n = r.data.created || 0;
        const nerr = (r.data.errors || []).length;
        toast.success(`Importate ${n} FAQ da ${emls.length} email${nerr ? ` · ${nerr} non riuscite` : ""}`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import .eml non riuscito");
    } finally { setImporting(false); }
  };

  // --- Allegato manuale nel form ---
  const onPickAttach = () => attachRef.current?.click();
  const onAttachSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !editing) return;
    setAttaching(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("token", editing.import_token);
        fd.append("file", file);
        const r = await api.post("/faq/attachment-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing((prev) => prev ? { ...prev, allegati: [...prev.allegati, { ...r.data, saved: false }] } : prev);
      }
      toast.success("Allegato aggiunto");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload allegato non riuscito");
    } finally { setAttaching(false); }
  };

  const removeAttach = async (a) => {
    if (!a.saved) {
      try { await api.delete(`/faq/attachment/${a.id}`); } catch { /* ignore */ }
    }
    setEditing((prev) => prev ? { ...prev, allegati: prev.allegati.filter((x) => x.id !== a.id) } : prev);
  };

  // Chiude il form scartando gli allegati "pending" non ancora salvati (evita file orfani).
  const closeForm = () => {
    if (editing) {
      const pending = editing.allegati.filter((a) => !a.saved);
      pending.forEach((a) => { api.delete(`/faq/attachment/${a.id}`).catch(() => {}); });
    }
    setEditing(null);
  };

  const q = query.trim().toLowerCase();
  const shown = q
    ? items.filter((it) => [it.oggetto, it.testo, it.circolare_nr, it.circolare_data, it.data]
        .some((v) => String(v || "").toLowerCase().includes(q)))
    : items;

  return (
    <div data-testid="faq-mensa-page" style={{ padding: 20, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontSize: 20, color: "#1e293b" }}>
            <HelpCircle size={22} color="#8e24aa" /> FAQ
          </h2>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
            Domande frequenti collegate alle circolari di bacheca · Anno {anno ? formatAnnoScolastico(anno) : "—"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <AnnoAttivoSelector />
          <button data-testid="faq-new-btn" onClick={openNew}
            style={btnStyle("#8e24aa")}><Plus size={16} /> Nuova FAQ</button>
          <button data-testid="faq-import-eml-btn" onClick={onPickEml} disabled={importing}
            style={btnStyle("#00838f", importing)}><Upload size={16} /> {importing ? "Import…" : "Importa .eml"}</button>
          <button data-testid="faq-import-spaggiari-btn" onClick={() => setSpaggiariOpen(true)}
            style={btnStyle("#5d4037")}><Download size={16} /> Importa da Spaggiari</button>
          <input ref={emlRef} type="file" accept=".eml" multiple onChange={onEmlSelected} style={{ display: "none" }} data-testid="faq-eml-input" />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 320px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }} />
          <input data-testid="faq-search-input" type="text" value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per oggetto, testo o circolare…"
            style={{ width: "100%", padding: "8px 30px 8px 32px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
          {query && (
            <button data-testid="faq-search-clear" onClick={() => setQuery("")} title="Pulisci"
              style={{ position: "absolute", right: 8, top: 8, background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={15} /></button>
          )}
        </div>
        <span style={{ fontSize: 12, color: "#64748b" }} data-testid="faq-count">
          {q ? `${shown.length} di ${items.length}` : `${items.length}`} FAQ
        </span>
      </div>

      <div style={{ background: "white", border: "1px solid #e3e8ef", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9", textAlign: "left", color: "#475569" }}>
              <th style={{ ...th, width: 110 }}>Data</th>
              <th style={th}>Oggetto</th>
              <th style={th}>Allegati</th>
              <th style={{ ...th, width: 130, textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="faq-table-body">
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>Caricamento…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }} data-testid="faq-empty">{q ? "Nessuna FAQ trovata per la ricerca." : "Nessuna FAQ per questo anno. Crea la prima con \"Nuova FAQ\"."}</td></tr>
            ) : shown.map((it) => (
              <tr key={it.id} data-testid={`faq-row-${it.id}`} style={{ borderTop: "1px solid #eef2f7", verticalAlign: "top" }}>
                <td style={td}>{fmtDate(it.data)}</td>
                <td style={{ ...td, fontWeight: 600, color: "#0f172a" }}>{it.oggetto || "—"}</td>
                <td style={{ ...td, maxWidth: 240 }}>
                  {(it.allegati || []).length ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {it.allegati.map((a) => (
                        <a key={a.id} data-testid={`faq-attach-dl-${a.id}`}
                          href={`${API_BASE}/faq/${it.id}/allegato/${a.id}`} target="_blank" rel="noreferrer"
                          title={`${a.filename} ${fmtSize(a.size) ? "· " + fmtSize(a.size) : ""}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#00838f", textDecoration: "none", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Paperclip size={12} style={{ flexShrink: 0 }} /> {a.filename}
                        </a>
                      ))}
                    </div>
                  ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                </td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button data-testid={`faq-detail-${it.id}`} onClick={() => setDetail(it)} title="Dettaglio"
                    style={{ ...iconBtn, color: "#2563eb" }}><Eye size={15} /></button>
                  <button data-testid={`faq-edit-${it.id}`} onClick={() => openEdit(it)} title="Modifica"
                    style={iconBtn}><Pencil size={15} /></button>
                  <button data-testid={`faq-delete-${it.id}`} onClick={() => remove(it)} title="Elimina"
                    style={{ ...iconBtn, color: "#dc2626" }}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div data-testid="faq-detail-modal" onClick={() => setDetail(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 14 }}>Dettaglio FAQ{detail.fonte === "eml" ? " · da email .eml" : ""}</strong>
              <button onClick={() => setDetail(null)} data-testid="faq-detail-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, maxHeight: "70vh", overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><div style={detLbl}>Data FAQ</div><div style={detVal}>{fmtDate(detail.data)}</div></div>
                <div><div style={detLbl}>Rif. circolare bacheca</div><div style={detVal}>{[detail.circolare_data ? fmtDate(detail.circolare_data) : "", detail.circolare_nr ? `n. ${detail.circolare_nr}` : ""].filter(Boolean).join(" · ") || "—"}</div></div>
              </div>
              <div><div style={detLbl}>Oggetto</div><div style={{ ...detVal, fontWeight: 700, color: "#0f172a" }} data-testid="faq-detail-oggetto">{detail.oggetto || "—"}</div></div>
              <div><div style={detLbl}>Testo</div>
                <div data-testid="faq-detail-testo" style={{ ...detVal, whiteSpace: "pre-wrap", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 8, padding: 12, color: "#334155" }}>{detail.testo || "—"}</div>
              </div>
              <div><div style={detLbl}>Allegati</div>
                {(detail.allegati || []).length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {detail.allegati.map((a) => (
                      <a key={a.id} data-testid={`faq-detail-attach-${a.id}`}
                        href={`${API_BASE}/faq/${detail.id}/allegato/${a.id}`} target="_blank" rel="noreferrer"
                        title={`${a.filename} ${fmtSize(a.size) ? "· " + fmtSize(a.size) : ""}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#00838f", textDecoration: "none" }}>
                        <Paperclip size={14} style={{ flexShrink: 0 }} /> {a.filename}{fmtSize(a.size) ? <span style={{ color: "#94a3b8" }}> · {fmtSize(a.size)}</span> : null}
                      </a>
                    ))}
                  </div>
                ) : <div style={detVal}>—</div>}
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid #eef2f7", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button data-testid="faq-detail-edit" onClick={() => { const d = detail; setDetail(null); openEdit(d); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #8e24aa", background: "white", color: "#8e24aa", fontWeight: 600, cursor: "pointer" }}><Pencil size={14} /> Modifica</button>
              <button data-testid="faq-detail-close-btn" onClick={() => setDetail(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#8e24aa", color: "white", fontWeight: 600, cursor: "pointer" }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}


      {editing && (
        <div data-testid="faq-form-modal" onClick={() => !saving && closeForm()}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 14 }}>{editing.id ? "Modifica FAQ" : "Nuova FAQ"}{editing.fonte === "eml" ? " · da email .eml" : ""}</strong>
              <button onClick={closeForm} data-testid="faq-form-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="Data FAQ">
                  <input data-testid="faq-field-data" type="date" value={editing.data}
                    onChange={(e) => setEditing({ ...editing, data: e.target.value })} style={inp} />
                </Field>
                <Field label="Data circolare bacheca">
                  <input data-testid="faq-field-circ-data" type="date" value={editing.circolare_data}
                    onChange={(e) => setEditing({ ...editing, circolare_data: e.target.value })} style={inp} />
                </Field>
                <Field label="N. circolare">
                  <input data-testid="faq-field-circ-nr" type="text" value={editing.circolare_nr} placeholder="es. 45"
                    onChange={(e) => setEditing({ ...editing, circolare_nr: e.target.value })} style={inp} />
                </Field>
              </div>
              <Field label="Oggetto">
                <input data-testid="faq-field-oggetto" type="text" value={editing.oggetto} placeholder="Titolo breve della FAQ"
                  onChange={(e) => setEditing({ ...editing, oggetto: e.target.value })} style={inp} />
              </Field>
              <Field label="Testo (scrivi o incolla)">
                <textarea data-testid="faq-field-testo" value={editing.testo} rows={8}
                  onChange={(e) => setEditing({ ...editing, testo: e.target.value })}
                  placeholder="Scrivi la risposta oppure incolla il testo dell'email…"
                  style={{ ...inp, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} />
              </Field>

              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Allegati</span>
                  <button data-testid="faq-add-attach-btn" onClick={onPickAttach} disabled={attaching}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", padding: "5px 10px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: attaching ? "wait" : "pointer" }}>
                    <Paperclip size={13} /> {attaching ? "Caricamento…" : "Aggiungi allegato"}
                  </button>
                  <input ref={attachRef} type="file" multiple onChange={onAttachSelected} style={{ display: "none" }} data-testid="faq-attach-input" />
                </div>
                {editing.allegati.length ? (
                  <div data-testid="faq-form-allegati" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {editing.allegati.map((a) => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, padding: "6px 10px" }}>
                        <Paperclip size={14} color="#00838f" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: "#0f172a", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                        {a.size ? <span style={{ fontSize: 11, color: "#94a3b8" }}>{fmtSize(a.size)}</span> : null}
                        {editing.id && a.saved ? (
                          <a href={`${API_BASE}/faq/${editing.id}/allegato/${a.id}`} target="_blank" rel="noreferrer"
                            style={{ color: "#00838f", display: "inline-flex" }} title="Scarica"><Download size={14} /></a>
                        ) : (
                          <button onClick={() => removeAttach(a)} data-testid={`faq-remove-attach-${a.id}`}
                            title="Rimuovi" style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", display: "inline-flex" }}><X size={14} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: 12, color: "#94a3b8" }}>Nessun allegato.</div>}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button onClick={closeForm} data-testid="faq-form-cancel"
                  style={{ background: "#f1f5f9", color: "#334155", border: "none", padding: "9px 16px", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Annulla</button>
                <button onClick={save} disabled={saving} data-testid="faq-form-save"
                  style={btnStyle("#8e24aa", saving)}>{saving ? "Salvataggio…" : "Salva"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {spaggiariOpen && (
        <div data-testid="faq-spaggiari-modal" onClick={() => setSpaggiariOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 460, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#5d4037", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 14 }}><FileText size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />Import da Spaggiari</strong>
              <button onClick={() => setSpaggiariOpen(false)} data-testid="faq-spaggiari-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <div style={{ padding: 20, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>
              L'importazione automatica dei messaggi dal portale <b>Spaggiari (ClasseViva)</b> è in fase di realizzazione.
              <div style={{ marginTop: 10, fontSize: 13, color: "#64748b" }}>
                Nel frattempo puoi incollare il testo manualmente con <b>Nuova FAQ</b> oppure importare l'email salvata come file <b>.eml</b> (con i suoi allegati).
              </div>
              <button onClick={() => setSpaggiariOpen(false)} data-testid="faq-spaggiari-ok"
                style={{ ...btnStyle("#5d4037"), marginTop: 16 }}>Ho capito</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label style={{ display: "block" }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>{label}</span>
    {children}
  </label>
);

const th = { padding: "10px 12px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 };
const td = { padding: "10px 12px" };
const inp = { width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, boxSizing: "border-box" };
const iconBtn = { background: "none", border: "none", cursor: "pointer", color: "#475569", padding: 4, marginLeft: 2 };
const detLbl = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#94a3b8", marginBottom: 3 };
const detVal = { fontSize: 14, color: "#334155" };
function btnStyle(color, disabled = false) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6, background: color, color: "white",
    border: "none", padding: "9px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13,
    cursor: disabled ? "wait" : "pointer", opacity: disabled ? 0.7 : 1,
  };
}
